import {
  useLazyGetNotificationsForUserQuery,
  useMarkNotificationAsViewedMutation, useMarkNotificationsAsViewedMutation
} from "../api/NotificationsApi";
import { Modal } from "../../ui/components/Modal";
import { useEffect, useState } from "react";
import { Notification } from "../models/Notification";
import { List } from "../../ui/components/List";
import { ListItem } from "../../ui/components/ListItem";
import classNames from "classnames";
import { t } from "i18next";
import { Link } from "../../ui/components/Link";
import { Trans } from "react-i18next";
import { useSlugs } from "../hooks/useSlugs";
import { NotificationTypes } from "../enums/NotificationTypes";
import {getDisplayDate} from "../../../utils/displayUtils";

export const NotificationsModal = () => {
  const {brandSlug, locationSlug} = useSlugs();
  const [get, result] = useLazyGetNotificationsForUserQuery();
  const [markNotificationAsViewed] = useMarkNotificationAsViewedMutation();
  const [markNotificationsAsViewed] = useMarkNotificationsAsViewedMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<Notification[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>();
  const [currentNotification, setCurrentNotification] = useState<Notification | undefined>(undefined);

  useEffect(() => {
    if (brandSlug && locationSlug) {
      setIsFetching(true);
      get({brandSlug, locationSlug});
    }
  }, [brandSlug, locationSlug, get]);

  useEffect(() => {
    if (!result.isLoading) {
      setIsFetching(false);
      if (result.isSuccess) {
        setData(result.data as Notification[]);
      }
    }
  }, [result]);

  useEffect(() => {
    if (!data?.length) {
      return;
    }
    const notification = data[0];
    setCurrentNotification(notification);
    setNotifications(data.slice(1));
    if (notification) {
      setIsOpen(true);
    }
  }, [data]);

  const handleOnClose = () => {
    setIsOpen(false);
    if (!!currentNotification?.id && currentNotification.notificationTypeId != NotificationTypes.Release) {
      if (currentNotification.chargebacks?.length) {
        const viewedNotifications = {
          chargebackIds: currentNotification.chargebacks.map(c => c.id),
        };
        markNotificationsAsViewed({notifications: viewedNotifications});
      }
      else {
        markNotificationAsViewed({notificationId: currentNotification.id});
      }
    }
    if (!notifications?.length) {
      return;
    }
    const notification = notifications[0];
    setNotifications(notifications.slice(1));
    if (notification) {
      setCurrentNotification(notification);
      setIsOpen(true);
    } else {
      setCurrentNotification(undefined);
    }
  };

  const handleViewRelease = (notificationId?: number) => {
    if (!notificationId) {
      return;
    }
    markNotificationAsViewed({notificationId});
  };

  if (isFetching || !data?.length || !currentNotification) {
    return null;
  }

  const chargebackDates = currentNotification?.chargebacks?.reduce(
    (acc, curr) => {
      if (curr.createdDate) {
        const currentDate = new Date(curr.createdDate);
        if (!acc.earliest || currentDate < new Date(acc.earliest)) {
          acc.earliest = curr.createdDate;
        }
        if (!acc.latest || currentDate > new Date(acc.latest)) {
          acc.latest = curr.createdDate;
        }
      }

      return acc;
    },
    { earliest: "" , latest: "" }
  );

  return (
    <div>
      {isOpen &&
        <Modal title={currentNotification.title ?? ""}
          onClose={handleOnClose}>
          <div>
            {currentNotification.body}
            {
              currentNotification.releases?.length &&
                <>
                  <List>
                    {
                      currentNotification.releases?.map((c, i) =>
                        <ListItem key={i} className={classNames(i === 0
                          ? "mt-2 md:mt-4"
                          : ""
                        )}>
                          <Link to={c.releaseNoteUrl ?? ""} isExternal={true}
                            onClick={() => handleViewRelease(c.notificationId)}
                            text={c.version ?? ""} variant="primary"/>
                        </ListItem>)
                    }
                  </List>
                  <div>
                    <Trans>
                      To view past release notes, click{" "}
                      <Link onClick={handleOnClose} variant="primary"
                        to={`/${brandSlug}/${locationSlug}/releases/list`}
                        text={t`here`}/>.
                    </Trans>
                  </div>
                </>
            }
            {
              currentNotification.chargebacks?.length &&
              <>
                <div>
                  <Trans>
                    These are the earliest and latest dates for the chargebacks to check in the chargeback report:
                  </Trans>
                </div>
                <List>
                  <ListItem className="mt-2 md:mt-4">
                    {getDisplayDate(chargebackDates?.earliest)} - {getDisplayDate(chargebackDates?.latest)}
                  </ListItem>
                </List>
              </>
            }
          </div>
        </Modal>
      }
    </div>
  );
};
